import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

const AuthForm = ({ isLogin = false, setIsAuthenticated }) => {
    const [formData, setFormData] = useState({ regNo: '', phone: '', otp: '' });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const navigate = useNavigate();
    const pollingInterval = useRef(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const stopPolling = () => {
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
        }
    };

    const startPolling = (reqId) => {
        stopPolling();
        pollingInterval.current = setInterval(async () => {
            try {
                const url = `${backendUrl}/auth/otp-status/${reqId}`;
                const response = await fetch(url);
                const result = await response.json();
                if (result.status !== 'pending') {
                    setStatus(result.status);
                    stopPolling();
                }
            } catch (err) {
                setStatus('error');
                setErrorMessage('Could not get OTP status. Please try again.');
                stopPolling();
            }
        }, 3000);
    };

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus('pending_validation');
        setErrorMessage('');
        setInfoMessage('');

        const url = isLogin ? `${backendUrl}/auth/login-request-otp` : `${backendUrl}/auth/signup-request-otp`;
        const body = { regNo: formData.regNo, phone: formData.phone };

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const result = await response.json();

            if (response.status === 202) {
                setInfoMessage("Validating your details...");
                startPolling(result.requestId);
            } else if (response.status === 409) {
                setStatus('otp_sent');
                setInfoMessage(result.message);
            } else {
                setErrorMessage(result.message);
                setStatus('idle');
            }
        } catch (err) {
            setErrorMessage('An error occurred. Please try again.');
            setStatus('idle');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');
        
        try {
            const url = `${backendUrl}/auth/verify-otp`;
            const response = await fetch(url, {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: formData.phone, otp: formData.otp })
            });
            const result = await response.json();
            
            if (result.success) {
                localStorage.setItem('token', result.token);
                setIsAuthenticated(true);
                
                // **THIS IS THE FIX:**
                // Pass the user data directly to the profile page to avoid a second API call.
                navigate('/profile', { state: { userProfile: result.user } });
            } else {
                setErrorMessage(result.message);
            }
        } catch (err) {
            setErrorMessage('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (status === 'sent') {
            setInfoMessage('An OTP has been sent to your WhatsApp!');
        } else if (status === 'invalid_number') {
            setErrorMessage('This is not a valid WhatsApp number. Please check it.');
            setStatus('idle');
        } else if (status === 'error') {
            setLoading(false);
        }
    }, [status]);
    
    useEffect(() => {
        return () => stopPolling();
    }, []);

    const showOtpForm = status === 'otp_sent' || status === 'sent';

    return (
        <div className='container'>
            <h1>{isLogin ? 'Student Login' : 'Student Signup'}</h1>
            <p>{showOtpForm ? `An OTP was sent to your WhatsApp.` : 'Enter your details to get a WhatsApp OTP'}</p>

            {infoMessage && <p className="form-message info">{infoMessage}</p>}
            {errorMessage && <p className="form-message error">{errorMessage}</p>}
            
            {showOtpForm ? (
                <form onSubmit={handleVerifyOtp}>
                    <div className='form-group'>
                        <label htmlFor='otp'>One-Time Password</label>
                        <input onChange={handleChange} type='text' name='otp' placeholder='Enter the 6-digit OTP...' value={formData.otp} maxLength="6" autoFocus/>
                    </div>
                    <button type='submit' disabled={loading}>
                        {loading ? <AiOutlineLoading3Quarters className="spinner" /> : 'Verify & Continue'}
                    </button>
                </form>
            ) : (
                <form onSubmit={handleRequestOtp}>
                    <div className='form-group'>
                        <label htmlFor='regNo'>Registration Number</label>
                        <input onChange={handleChange} type='text' name='regNo' placeholder='Enter your 11-digit Reg. No...' value={formData.regNo} maxLength="11" />
                    </div>
                    <div className='form-group'>
                        <label htmlFor='phone'>WhatsApp Number</label>
                        <div className="input-with-icon">
                            <FaWhatsapp className="icon" />
                            <input onChange={handleChange} type='text' name='phone' placeholder='Enter your 10-digit number...' value={formData.phone} maxLength="10" />
                        </div>
                    </div>
                    <button type='submit' disabled={loading}>
                        {loading ? <AiOutlineLoading3Quarters className="spinner" /> : (isLogin ? 'Login with OTP' : 'Send OTP')}
                    </button>
                </form>
            )}

            <div className='form-link'>
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <Link to={isLogin ? "/signup" : "/login"}>{isLogin ? "Signup" : "Login"}</Link>
            </div>
        </div>
    );
};
export default AuthForm;
